import {
    ColumnType,
    DataGridTooltipedFormatter,
    DropDown,
    getFilterElementsDefault,
    getMaskedPhone,
    getMaskFromName,
    PhoneNumber,
    sharpDateFormatter,
    useErrorState,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import {
    addMerchantOrder,
    getChannels,
    getMerchantContacts,
    getMerchantOrder,
    getMerchantOrders,
    getMerchantOrdersExcel,
    getOutlets,
    getSettings,
    getTariffGroups,
    getUserHasActiveOutlets,
} from 'axios/merchantApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import { subDays } from 'date-fns';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    MerchantFilterType,
    MerchantItem,
    MerchantOrderAdd,
    MerchantTableItem,
    MerchantTradeOrgContact,
} from 'models/api/merchant';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useNotifyContext } from 'providers/NotifyProvider';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { Col, Row } from 'reactstrap';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import './Merchant.scss';
import MerchantContacts from './MerchantContacts';
import MerchantFilter from './MerchantFilter';
import MerchantOrderAddButton from './MerchantOrderAddButton';
import MerchantOrderModal from './MerchantOrderModal/MerchantOrderModal';
import MerchantStatistic from './MerchantStatistic';
import { getOrderStatusesSelectList } from 'axios/orderStatusApi';
import { moneyFormatter, dateTimeFormatter } from 'utils/columnFormatters';

const Merchant = () => {
    const api = useApi();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();
    const { dateTimeFormat } = useGeneralSettingsContext();
    const { addSubscription, removeSubscription } = useNotifyContext();
    const queryClient = useQueryClient();
    const { t, i18n } = useTranslation();

    const [contacts, setContacts] = useState<MerchantTradeOrgContact[]>([]);
    const [isModalVisible, setModalVisible] = useState(false);
    const [orderStatuses, setOrderStatuses] = useState<DropDown[]>([]);
    const [sources, setSources] = useState<DropDown[]>([]);

    useEffect(() => {
        queryClient.invalidateQueries([['merchant']]);
    }, [i18n.language]);

    useEffect(() => {
        addSubscription({
            key: 'onToOrderStatusChange',
            type: 'ToOrderStatusChange',
            callback: () => {
                queryClient.invalidateQueries([['merchant']]);
            },
        });

        return () => {
            removeSubscription('onToOrderStatusChange');
        };
    }, []);

    const getOrderStatusesQuery = useQuery(
        ['orderStatusesSelectList', i18n.language],
        () => getOrderStatusesSelectList(api, false),
        {
            onSuccess: (data) => setOrderStatuses(data),
            onError: (err) => handleRequestError(err),
        }
    );

    const getDictsQuery = useQuery(
        ['merchantDicts', i18n.language],
        () => getSelectList(api, ['TOIntegrationType']),
        {
            onSuccess: (data) => data?.TOIntegrationType && setSources(data.TOIntegrationType),
            onError: (err) => handleRequestError(err),
        }
    );

    const getMerchantContactsQuery = useQuery('merchantContacts', () => getMerchantContacts(api), {
        onSuccess: (data) => setContacts(data),
        onError: (err) => handleRequestError(err),
    });

    const { isFetching: settingsFetching, data: settings } = useQuery(
        'merchantSettings',
        () => getSettings(api),
        {
            onError: (err) => handleRequestError(err),
            enabled: hasRole('merchantGetSettings'),
        }
    );

    const { isFetching: isFetchingChannels, data: channels } = useQuery(
        'merchantChannels',
        () => getChannels(api, true),
        {
            onError: (err) => handleRequestError(err),
            enabled: hasRole('merchantGetChannels'),
        }
    );

    const { isFetching: isFetchingOutlets, data: outlets } = useQuery(
        'merchantOutlets',
        () => getOutlets(api, true),
        {
            onError: (err) => handleRequestError(err),
            enabled: hasRole('merchantGetOutletsDropDown'),
        }
    );

    const { isFetching: tariffGroupsFetching, data: tariffGroups } = useQuery(
        'merchantTariffGroups',
        () => getTariffGroups(api),
        {
            onError: (err) => handleRequestError(err),
        }
    );

    const { isFetched: hasActiveOutletFetched, data: hasActiveOutlets } = useQuery(
        'merhchantHasActiveOutlets',
        () => getUserHasActiveOutlets(api),
        {
            onError: (err) => handleRequestError(err),
            enabled: hasRole('merchantOrderCreate'),
        }
    );

    const { data: brandCodeResponse, isFetched: isFetchingBrandCodes } = useQuery(
        'brandCodes',
        () => getSelectList(api, ['BrandCode']),
        {
            onError: (err) => handleRequestError(err),
            enabled: hasRole('merchantBrandCodeFilter'),
        }
    );
    const brandCodes = useMemo<DropDown[]>(() => {
        return brandCodeResponse?.['BrandCode'] ?? [];
    }, [brandCodeResponse]);

    const initColumns: ColumnType<MerchantTableItem, MerchantTableItem[]>[] = [
        {
            key: 'fio',
            name: <Trans i18nKey={'BackOfficeClient'} />,
            cellClass: 'normalLineHeight',
            formatter: ({ row: { fio, phoneNumber } }) => {
                const phoneFormatted = getMaskedPhone(phoneNumber, getMaskFromName('Default'));

                return (
                    <DataGridTooltipedFormatter
                        tooltipText={`${fio} ${phoneFormatted}`}
                        overflownElementClassName="text-ellipsis"
                        className="pt-2"
                    >
                        {fio}
                        <div className="small font-weight-light text-ellipsis">
                            <PhoneNumber value={phoneNumber} />
                        </div>
                    </DataGridTooltipedFormatter>
                );
            },
        },
        {
            key: 'id',
            columnsForSorting: [
                {
                    key: 'visibleId',
                    name: 'BackOfficeId',
                },
                {
                    key: 'dt',
                    name: 'BackOfficeCreateDate',
                },
            ],
            name: <Trans i18nKey={'BackOfficeOrderId'} />,
            cellClass: 'normalLineHeight',
            formatter: ({ row: { id, dt, orderId } }) => (
                <DataGridTooltipedFormatter
                    tooltipText={`${orderId ?? id} ${sharpDateFormatter(dt, dateTimeFormat)}`}
                    overflownElementClassName="text-ellipsis"
                    className="pt-2"
                >
                    #{orderId ?? id}
                    <div className="small font-weight-light text-ellipsis">
                        <DataTimeText value={dt} />
                    </div>
                </DataGridTooltipedFormatter>
            ),
        },
        {
            key: 'dtSell',
            name: <Trans i18nKey={'BackOfficeDtSell'} />,
            cellClass: 'normalLineHeight',
            formatter: dateTimeFormatter('dtSell'),
        },
        {
            key: 'orderSum',
            name: <Trans i18nKey={'BackOfficeSumTotal'} />,
            cellClass: 'normalLineHeight',
            formatter: moneyFormatter('orderSum'),
        },
        {
            key: 'merchantReceives',
            name: <Trans i18nKey={'BackOfficeMerchantReceives'} />,
            cellClass: 'normalLineHeight',
            formatter: moneyFormatter('merchantReceives'),
        },
        {
            key: 'merchantTariffProcentSum',
            name: <Trans i18nKey={'BackOfficeMerchantTariffProcentSum'} />,
            cellClass: 'normalLineHeight',
            formatter: moneyFormatter('merchantTariffProcentSum'),
        },
        {
            key: 'statusName',
            name: <Trans i18nKey={'BackOfficeOrderStatus'} />,
            cellClass: 'normalLineHeight',
        },
        {
            key: 'tariffGroupName',
            name: <Trans i18nKey={'BackOfficeTariff'} />,
            cellClass: 'normalLineHeight',
        },
        {
            key: 'outletName',
            name: <Trans i18nKey={'BackOfficeReportHeaderOutletAddress'} />,
            cellClass: 'normalLineHeight',
        },
        {
            key: 'source',
            name: <Trans i18nKey={'BackOfficeSource'} />,
            cellClass: 'normalLineHeight',
        },
        {
            key: 'channel',
            name: <Trans i18nKey={'BackOfficeChannel'} />,
            cellClass: 'normalLineHeight',
        },
    ];

    return (
        <Row className="merchant-orders">
            <Col xs={hasRole(['merchantTradeOrgGetById', 'merchantStatisticGet']) ? '9' : '12'}>
                <BOWorkplace<
                    MerchantTableItem,
                    MerchantItem,
                    MerchantFilterType,
                    MerchantItem,
                    MerchantOrderAdd
                >
                    pageTitle="BackOfficeMerchant"
                    defaultPageSize={10}
                    filterParams={{
                        sd: getStartOfTheDay(subDays(new Date(), 30)),
                        ed: getEndOfTheDay(),
                        orderInfo: '',
                        statusCode: '',
                        source: '',
                        outletId: '',
                        channel: '',
                        brandCode: '',
                    }}
                    initColumns={initColumns}
                    storagePath="merchant/columns"
                    lineHeight={60}
                    rowClass={(row: MerchantTableItem) => {
                        if (row.statusCode === 'goodship') return 'merchant__confirm_merchant_row';
                        if (
                            row.statusCode === 'ficancel' ||
                            row.statusCode === 'declined' ||
                            row.statusCode === 'declinedLimit'
                        ) {
                            return 'merchant__declined_merchant_row';
                        }
                    }}
                    showExcelBtn={hasRole('merchantExcel')}
                    fetchExcelQueryFn={(filter) => getMerchantOrdersExcel(api, filter)}
                    fetchQueryFn={(filter) => getMerchantOrders(api, filter)}
                    buttons={[
                        hasRole('merchantOrderCreate') &&
                        hasActiveOutletFetched &&
                        hasActiveOutlets ? (
                            <MerchantOrderAddButton
                                key="1"
                                showModal={() => setModalVisible(true)}
                            />
                        ) : null,
                    ]}
                    getFilterElements={getFilterElementsDefault([
                        {
                            id: 'sd',
                            formatter: (val) =>
                                `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(
                                    val,
                                    dateTimeFormat
                                )}`,
                        },
                        {
                            id: 'ed',
                            formatter: (val) =>
                                `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                        },
                        { name: t('BackOfficeOrderInfo'), id: 'orderInfo' },
                        {
                            id: 'statusCode',
                            name: t('BackOfficeOrderStatus'),
                            formatter: (val) => {
                                const code = orderStatuses.find((x) => x.id === val)?.name;
                                return `${t('BackOfficeOrderStatus')}: ${code}`;
                            },
                        },
                        {
                            id: 'source',
                            name: t('BackOfficeSource'),
                            formatter: (val) =>
                                sources?.find((t) => t.id === val)?.name ?? val.toString(),
                        },

                        {
                            id: 'outletId',
                            name: t('BackOfficeReportHeaderOutletAddress'),
                            formatter: (val) =>
                                outlets?.find((o) => o.id === val)?.name ?? val.toString(),
                        },
                        {
                            id: 'channel',
                            name: t('BackOfficeChannel'),
                            formatter: (val) =>
                                channels?.find((t) => t.id === val)?.name ?? val.toString(),
                        },
                        {
                            id: 'brandCode',
                            name: t('BackOfficeBrandCode'),
                            formatter: (val) =>
                                brandCodes?.find((o) => o.id === val)?.name ?? val.toString(),
                        },
                    ])}
                    showModal={
                        hasRole('merchantOrderGetById') ||
                        (hasRole('merchantOrderCreate') &&
                            hasActiveOutletFetched &&
                            hasActiveOutlets)
                            ? () => setModalVisible(true)
                            : undefined
                    }
                    filter={
                        <MerchantFilter
                            getOrderStatusesQuery={getOrderStatusesQuery}
                            getDictsQuery={getDictsQuery}
                            orderStatuses={orderStatuses}
                            sources={sources}
                            outlets={outlets ?? []}
                            channels={channels ?? []}
                            brandCodes={brandCodes ?? []}
                            isFetchingBrandCodes={isFetchingBrandCodes}
                            isFetchingOutlets={isFetchingOutlets}
                            isFetchingChannels={isFetchingChannels}
                        />
                    }
                    modal={{
                        component: isModalVisible && hasRole('merchantOrderCreate') && (
                            <MerchantOrderModal
                                isOpen={isModalVisible}
                                toggle={() => setModalVisible(false)}
                                contacts={contacts ?? []}
                                getContactsQuery={getMerchantContactsQuery}
                                tariffGroups={tariffGroups ?? []}
                                tariffGroupsFetching={tariffGroupsFetching}
                                settings={settings}
                                settingsFetching={settingsFetching}
                            />
                        ),
                        fetchCurrentItemQueryFn: (id) => getMerchantOrder(api, id),
                        addMutationFn: (item) => addMerchantOrder(api, item),
                    }}
                    queryKey={['merchant']}
                    queryClient={queryClient}
                />
            </Col>
            <Col xs="3" hidden={!hasRole(['merchantTradeOrgGetById', 'merchantStatisticGet'])}>
                <MerchantStatistic />
                <MerchantContacts contacts={contacts} getContactsQuery={getMerchantContactsQuery} />
            </Col>
        </Row>
    );
};

const useMerchantWorkplaceState = () =>
    useWorkplaceState<MerchantTableItem, MerchantItem, MerchantFilterType, any, MerchantOrderAdd>();

export default Merchant;
export { useMerchantWorkplaceState };
